import '../screens.css';
import './UserGuide.css';

/**
 * User Guide.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/user-guide.ejs` — 1,095
 * lines of hand-written static HTML with no data binding and no logic to reimplement.
 *
 * EVERY SECTION'S PROSE IS NOW ACROSS, copied from the source rather than paraphrased:
 * the feature cards with their tick-lists, the MVP and NPV formulas, the NPV Value
 * Strength bands, Administration, and the six Tips. Wording, capitalisation and the
 * formulas' exact form are the source's.
 *
 * The sections are filtered to THIS product. The source's guide covers one application
 * whose scope runs from discovery through delivery tracking; its entries for Strategic
 * Intelligence, Effort & Value, Roadmap Planning, Program Management and the two
 * dashboards describe screens that live in other products on this platform, so they are
 * not listed here as if this guide explained them. Portfolio Optimizer and Administration
 * ARE here, because both exist in Inventory Registry.
 *
 * The source's "Need Additional Help?" block is not repeated: this platform has its own
 * Help & Support screen, which is where that content lives (and which the nav links to).
 */

interface FeatureCard {
  icon: string;
  title: string;
  body: string;
  points: string[];
}

interface Tip {
  title: string;
  body: string;
}

interface Section {
  id: string;
  icon: string;
  label: string;
  heading: string;
  intro?: string;
  features?: FeatureCard[];
  formula?: { title: string; icon: string; lines: string[]; note?: string[] };
  bands?: { score: string; label: string; body: string }[];
  tips?: Tip[];
}

const SECTIONS: Section[] = [
  {
    id: 'quick-start',
    icon: '🚀',
    label: 'Quick Start',
    heading: 'Quick Start Guide',
    intro: 'Get started with opportunity scoring in just 4 simple steps:',
  },
  {
    id: 'workflow',
    icon: '🌳',
    label: 'Workflow Overview',
    heading: 'Scoring Workflow',
    intro:
      'An opportunity moves Draft → Approved → Ready for Scoring → Scoring in Progress → Scored. Each transition is deliberate: approval commits to assessing it, scoring locks the framework so answers stay meaningful, and a score can only be revised by an explicit re-score.',
  },
  {
    id: 'opportunities',
    icon: '💡',
    label: 'Managing Opportunities',
    heading: 'Core Features',
    features: [
      {
        icon: 'lightbulb',
        title: 'Opportunity Management',
        body: 'Centralize all your opportunities in one place. Track status, ownership, and progress through the assessment process.',
        points: [
          'Create and edit opportunities',
          'Assign to projects and owners',
          'Track status (New, In Progress, Scored)',
          'Add descriptions and business context',
          'Set estimated costs and benefits',
        ],
      },
      {
        icon: 'analytics',
        title: 'Scoring Framework',
        body: 'Evaluate opportunities using structured frameworks with weighted categories and criteria for consistent, objective assessment.',
        points: [
          'Multiple scoring frameworks available',
          'Weighted categories for prioritization',
          '1-5 scale scoring with descriptions',
          'Save progress and continue later',
          'Automatic score calculation',
        ],
      },
      {
        icon: 'pie_chart',
        title: 'Portfolio Optimizer',
        body: 'Optimize your portfolio by analyzing scored opportunities against budget constraints and strategic priorities.',
        points: [
          'Set budget constraints',
          'Adjust category weights',
          'Compare multiple scenarios',
          'Visualize portfolio distribution',
          'Export optimization results',
        ],
      },
      {
        icon: 'assessment',
        title: 'Reports & Analytics',
        body: 'Generate comprehensive reports and visualizations to support decision-making and stakeholder communication.',
        points: [
          'Score breakdown by category',
          'Comparative analysis charts',
          'Export to CSV/PDF formats',
          'Dashboard visualizations',
          'Historical trend analysis',
        ],
      },
      {
        icon: 'folder_special',
        title: 'Project Management',
        body: 'Organize opportunities into projects for better governance and framework assignment.',
        points: [
          'Create and manage projects',
          'Assign scoring frameworks',
          'Group related opportunities',
          'Track project-level metrics',
          'Set project owners',
        ],
      },
      {
        icon: 'admin_panel_settings',
        title: 'Administration',
        body: 'Manage users, roles, and system settings with comprehensive administrative controls.',
        points: [
          'User management',
          'Role-based access control',
          'Framework configuration',
          'System settings',
          'Audit logging',
        ],
      },
    ],
  },
  {
    id: 'scoring',
    icon: '📊',
    label: 'Scoring Process',
    heading: 'Understanding the Scoring Scale',
    intro:
      'Every question is answered on a 1–5 scale. Each answer is weighted by its component’s weight, and the weighted total is expressed back on the same 1–5 scale, so a score is always comparable across opportunities scored with the same framework.',
  },
  {
    id: 'mvp-cost',
    icon: '🧮',
    label: 'MVP Cost Calculation',
    heading: 'MVP Cost Calculation',
    intro:
      'The MVP (Minimum Viable Product) Cost Calculation module helps you estimate delivery costs, calculate ROI, and determine payback periods for your opportunities.',
    features: [
      {
        icon: 'tune',
        title: 'Cost Scenarios',
        body: 'Define multiple cost scenarios (Base, Aggressive, Conservative) to model different delivery approaches and risk profiles.',
        points: [
          'Create multiple scenario versions',
          'Configure POD templates and phases',
          'Set delivery timelines and FTE allocations',
          'Compare scenarios side-by-side',
        ],
      },
      {
        icon: 'account_balance',
        title: 'Financial Summary',
        body: 'View comprehensive financial metrics including build costs, run rates, and ROI calculations for each opportunity.',
        points: [
          'Current vs Future run rate comparison',
          'Annual savings calculation',
          'ROI percentage and payback period',
          'MVP Value Strength auto-calculation',
        ],
      },
      {
        icon: 'compare_arrows',
        title: 'Version Comparison',
        body: 'Compare different financial model versions to understand the impact of delivery choices on costs and returns.',
        points: [
          'Side-by-side version comparison',
          'Highlight differences in key metrics',
          'Track version history',
          'Export comparison reports',
        ],
      },
    ],
    formula: {
      icon: 'lightbulb',
      title: 'Key Formula',
      lines: [
        'Annual Savings = Current Run Rate - Future Run Rate - Hosting Costs - Support Costs',
        'ROI % = (Annual Savings / Build Cost) × 100',
        'Payback Period = Build Cost / Annual Savings (in years)',
      ],
    },
  },
  {
    id: 'npv-cost',
    icon: '🏦',
    label: 'NPV Cost Calculation',
    heading: 'NPV Cost Calculation',
    intro:
      'The NPV (Net Present Value) Cost Calculation module provides advanced financial analysis using time-value-of-money principles to evaluate the long-term economic viability of opportunities.',
    features: [
      {
        icon: 'settings_applications',
        title: 'Scenario Setup',
        body: 'Configure NPV-specific scenarios with investment timelines, discount rates, and cash flow projections.',
        points: [
          'Define NPV scenario parameters',
          'Set discount rates (WACC-based)',
          'Configure investment horizons (1-10 years)',
          'Model cash flow timing',
        ],
      },
      {
        icon: 'functions',
        title: 'NPV Calculator',
        body: 'Calculate Net Present Value with configurable discount rates and time horizons for accurate investment evaluation.',
        points: [
          'Automated NPV calculation',
          'Value classification (High/Medium/Low)',
          'NPV Value Strength scoring (1-5)',
          'Persist results to opportunity',
        ],
      },
      {
        icon: 'price_change',
        title: 'NPV Cost Management',
        body: 'Manage all cost components that feed into NPV calculations, including build costs, operational costs, and benefits.',
        points: [
          'Initial investment (build costs)',
          'Annual operational costs',
          'Expected annual benefits',
          'Net annual cash flow calculation',
        ],
      },
      {
        icon: 'analytics',
        title: 'Scenario Comparison',
        body: 'Compare NPV scenarios across different opportunities to identify the most economically attractive investments.',
        points: [
          'Multi-opportunity comparison',
          'Visual NPV breakdown charts',
          'Sensitivity analysis support',
          'Export comparison data',
        ],
      },
    ],
    formula: {
      icon: 'functions',
      title: 'NPV Formula',
      lines: ['NPV = -Initial Investment + Σ (Net Annual Cash Flow / (1 + r)^t)'],
      note: [
        'Where:',
        '• r = Discount rate (e.g., 10% = 0.10)',
        '• t = Year (1 to horizon years)',
        '• Net Annual Cash Flow = Annual Benefits - Annual Costs',
      ],
    },
    bands: [
      {
        score: 'Score 1',
        label: 'Weak/Non-viable',
        body: 'NPV is negative or low. May not justify investment.',
      },
      {
        score: 'Score 3',
        label: 'Moderate',
        body: 'NPV is positive with moderate value. Candidate for refinement.',
      },
      { score: 'Score 5', label: 'Strong', body: '' },
    ],
  },
  {
    id: 'portfolio',
    icon: '🥧',
    label: 'Portfolio Optimizer',
    heading: 'Portfolio Optimizer',
    intro:
      'Optimize your portfolio by analyzing scored opportunities against budget constraints and strategic priorities.',
    features: [
      {
        icon: 'pie_chart',
        title: 'Portfolio Optimizer',
        body: 'Optimize your portfolio by analyzing scored opportunities against budget constraints and strategic priorities.',
        points: [
          'Set budget constraints',
          'Adjust category weights',
          'Compare multiple scenarios',
          'Visualize portfolio distribution',
          'Export optimization results',
        ],
      },
    ],
  },
  {
    id: 'admin',
    icon: '⚙️',
    label: 'Administration',
    heading: 'Administration & Settings',
    intro:
      'Configure the platform and manage access. Most administrative tasks live under Settings and are restricted to Administrators.',
    features: [
      {
        icon: 'people',
        title: 'Users & Roles',
        body: 'Manage user accounts and roles (Administrator, Strategic Analyst, …) that govern what each person can see and do.',
        points: [],
      },
      {
        icon: 'cloud',
        title: 'Integrations',
        body: 'Connect document storage and other services to the platform.',
        points: [],
      },
      {
        icon: 'tune',
        title: 'Frameworks & Q&A',
        body: 'Maintain scoring frameworks, dimensions, and the question catalog used across the platform.',
        points: [],
      },
    ],
  },
  {
    id: 'tips',
    icon: '💡',
    label: 'Tips & Best Practices',
    heading: 'Tips & Best Practices',
    tips: [
      {
        title: 'Be Specific with Descriptions',
        body: 'Include clear business context, expected outcomes, and success metrics in your opportunity descriptions for better stakeholder alignment.',
      },
      {
        title: 'Involve Subject Matter Experts',
        body: 'Collaborate with technical and business SMEs when scoring to ensure accurate assessments across all categories.',
      },
      {
        title: 'Save Progress Regularly',
        body: 'Use the "Save Progress" button to preserve your answers. You can return and complete scoring at any time.',
      },
      {
        title: 'Use Portfolio Optimizer',
        body: 'After scoring multiple opportunities, use the Portfolio Optimizer to identify the best combination within your budget constraints.',
      },
      {
        title: 'Review Score Breakdowns',
        body: 'Analyze category-level scores to identify specific areas of strength and improvement for each opportunity.',
      },
      {
        title: 'Keep Estimates Realistic',
        body: 'Provide realistic delivery cost and business benefit estimates to enable accurate ROI calculations and portfolio optimization.',
      },
    ],
  },
];

const QUICK_START = [
  {
    n: 1,
    title: 'Define the process hierarchy',
    desc: 'Settings → Business Process Definition. L1 groups, L2 areas and L3 activities give every opportunity a place to sit.',
  },
  {
    n: 2,
    title: 'Build or pick a framework',
    desc: 'Settings → Assessment Framework. A framework selects dimensions, components and questions. Mark it Design Complete before scoring against it.',
  },
  {
    n: 3,
    title: 'Capture opportunities',
    desc: 'Opportunities → Opportunity Registry. Create them, assign them to a project, and set their business attributes.',
  },
  {
    n: 4,
    title: 'Score and decide',
    desc: 'Send an approved opportunity to scoring, answer every question, and the platform produces a weighted score and its recommendation.',
  },
];

export function UserGuide() {
  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">
            User Guide<span className="ug-badge">v2.0</span>
          </h1>
          <p className="pd-page-header__subtitle">
            Complete guide to using the platform effectively
          </p>
        </div>
      </div>

      <div className="ug-toc">
        <p className="ug-toc-title">Quick Navigation</p>
        <ul className="ug-toc-list">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`}>
                <span aria-hidden="true">{s.icon}</span>
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {SECTIONS.map((s) => (
        <section key={s.id} id={s.id} className="ug-card">
          <h2>{s.heading}</h2>
          {s.intro ? <p>{s.intro}</p> : null}

          {s.id === 'quick-start' ? (
            <div className="ug-steps">
              {QUICK_START.map((q) => (
                <div key={q.n} className="ug-step">
                  <div className="ug-step-n">{q.n}</div>
                  <div className="ug-step-t">{q.title}</div>
                  <div className="ug-step-d">{q.desc}</div>
                </div>
              ))}
            </div>
          ) : null}

          {s.features ? (
            <div className="ug-features">
              {s.features.map((f) => (
                <div className="ug-feature" key={f.title}>
                  <div className="ug-feature-hd">
                    <i className="material-icons">{f.icon}</i>
                    <h3>{f.title}</h3>
                  </div>
                  <p>{f.body}</p>
                  {f.points.length ? (
                    <ul className="ug-ticks">
                      {f.points.map((p) => (
                        <li key={p}>
                          <i className="material-icons">check_circle</i>
                          {p}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {s.formula ? (
            <div className="ug-formula">
              <div className="ug-formula-hd">
                <i className="material-icons">{s.formula.icon}</i>
                {s.formula.title}
              </div>
              {s.formula.lines.map((l) => (
                <code key={l}>{l}</code>
              ))}
              {s.formula.note ? (
                <div className="ug-formula-note">
                  {s.formula.note.map((n) => (
                    <div key={n}>{n}</div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {s.bands ? (
            <div className="ug-bands">
              <div className="ug-bands-hd">
                <i className="material-icons">info</i>
                NPV Value Strength Interpretation
              </div>
              {s.bands.map((b) => (
                <div className="ug-band" key={b.score}>
                  <div className="ug-band-t">
                    {b.score} — {b.label}
                  </div>
                  {b.body ? <div className="ug-band-d">{b.body}</div> : null}
                </div>
              ))}
            </div>
          ) : null}

          {s.tips ? (
            <div className="ug-tips">
              {s.tips.map((t) => (
                <div className="ug-tip" key={t.title}>
                  <i className="material-icons">lightbulb</i>
                  <div>
                    <div className="ug-tip-t">{t.title}</div>
                    <div className="ug-tip-d">{t.body}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}
